'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { User, LogOut, Settings, Compass, Bookmark, Heart, Bell, LayoutDashboard, Command, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { SearchesAndAlertsPanel } from '@/components/search'
import { useAuth } from './auth-provider'
import { useSubscription } from '@/lib/subscription'
import { useUIStore } from '@/lib/store'
import { useI18n } from '@/lib/i18n'

export function UserMenu() {
  const { user, loading, signIn, signOut } = useAuth()
  const { t } = useI18n()
  const { isPenthouse } = useSubscription()
  const { setCommandPaletteOpen } = useUIStore()
  const router = useRouter()
  const [savedSearchesOpen, setSavedSearchesOpen] = React.useState(false)

  // ⌘K hint — the header's palette button is hidden on /search (no width
  // beside the 50vw input) and below xl everywhere, so this menu is the
  // palette's only affordance on those surfaces.
  const [isMac, setIsMac] = React.useState(false)
  React.useEffect(() => {
    setIsMac(/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform))
  }, [])

  // Top-bar navigation, mirrored into the avatar menu so every destination is
  // reachable here too (esp. on mobile, where the header pills collapse).
  const NAV: { href: string; icon: LucideIcon; label: string }[] = [
    { href: '/search', icon: Compass, label: t('header.explore', 'Explore') },
    { href: '/searches', icon: Bookmark, label: t('header.searches', 'Searches') },
    { href: '/liked', icon: Heart, label: t('header.likedFlats', 'Liked flats') },
    { href: '/notifications', icon: Bell, label: t('notifications.whatsNew', "What's new") },
    { href: '/my-listings', icon: LayoutDashboard, label: t('header.myListings', 'My listings') },
  ]

  // Dispatch custom event when a saved search is loaded so page.tsx can trigger search.
  // If not on /search, navigate there first — the search page's init effect will pick up
  // the zustand state that was already set by handleLoadSearch in saved-searches-panel.
  const handleLoadSearch = React.useCallback((options?: { newSince?: string }) => {
    if (window.location.pathname !== '/search') {
      // Store newSince so the search page can pick it up after mount
      if (options?.newSince) {
        sessionStorage.setItem('roostr:newSince', options.newSince)
      }
      router.push('/search')
    } else {
      window.dispatchEvent(new CustomEvent('saved-search-loaded', {
        detail: options ? { newSince: options.newSince } : undefined
      }))
    }
  }, [router])

  // Listen for open saved searches panel event (from command palette or roostr roster)
  React.useEffect(() => {
    const handleOpenPanel = () => {
      if (user) {
        setSavedSearchesOpen(true)
      }
    }

    window.addEventListener('open-saved-searches-panel', handleOpenPanel)
    window.addEventListener('open-roostr-roster-panel', handleOpenPanel)
    return () => {
      window.removeEventListener('open-saved-searches-panel', handleOpenPanel)
      window.removeEventListener('open-roostr-roster-panel', handleOpenPanel)
    }
  }, [user])

  if (loading) {
    return (
      <div className="w-10 h-10 rounded-full bg-muted animate-pulse" />
    )
  }

  if (!user) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={() => signIn()}
        className="rounded-full sm:px-4 sm:gap-2 sm:w-auto"
        title={t('userMenu.signIn', 'Sign in')}
      >
        <User className="h-5 w-5" />
        <span className="hidden sm:inline">{t('userMenu.signIn', 'Sign in')}</span>
      </Button>
    )
  }

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button className="relative flex items-center gap-2 rounded-full hover:bg-muted p-1 transition-colors outline-none">
            {isPenthouse && (
              <span className="absolute inset-0 rounded-full animate-crown-ring bg-[conic-gradient(from_0deg,#f59e0b,#fbbf24,#f59e0b,#d97706,#f59e0b)] opacity-80" />
            )}
            <Avatar
              src={user.photoURL}
              alt={user.displayName || 'User'}
              fallback={user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
              size="sm"
              className={isPenthouse ? 'ring-2 ring-background relative' : undefined}
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={cn(
              'min-w-[220px] rounded-xl p-1.5',
              'bg-popover/95 backdrop-blur-xl border border-border/40 shadow-lg',
              'animate-in fade-in-0 zoom-in-95',
              'z-50'
            )}
            sideOffset={8}
            align="end"
          >
            {/* User info */}
            <div className="px-3 py-2.5 border-b border-border/30 mb-1">
              <p className="font-medium text-sm truncate">{user.displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>

            {/* All top-bar navigation, mirrored here. */}
            {NAV.map((item) => {
              const Icon = item.icon
              return (
                <DropdownMenu.Item
                  key={item.href}
                  onClick={() => router.push(item.href)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm rounded-lg',
                    'cursor-pointer outline-none',
                    'hover:bg-accent focus:bg-accent'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </DropdownMenu.Item>
              )
            })}

            <DropdownMenu.Separator className="h-px bg-border/40 my-1" />

            {/* Command palette — same intent as the mirrored nav above: the
                header button collapses (always on /search, and below xl
                everywhere), so this row is where the shortcut is taught. */}
            <DropdownMenu.Item
              onClick={() => setCommandPaletteOpen(true)}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm rounded-lg',
                'cursor-pointer outline-none',
                'hover:bg-accent focus:bg-accent'
              )}
            >
              <Command className="h-4 w-4" />
              {t('userMenu.commandPalette', 'Command palette')}
              <kbd className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {isMac ? '⌘K' : 'Ctrl K'}
              </kbd>
            </DropdownMenu.Item>

            <DropdownMenu.Item
              onClick={() => router.push('/settings')}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm rounded-lg',
                'cursor-pointer outline-none',
                'hover:bg-accent focus:bg-accent'
              )}
            >
              <Settings className="h-4 w-4" />
              {t('userMenu.settings', 'Settings')}
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="h-px bg-border/40 my-1" />

            <DropdownMenu.Item
              onClick={signOut}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm rounded-lg',
                'cursor-pointer outline-none text-destructive',
                'hover:bg-destructive/10 focus:bg-destructive/10'
              )}
            >
              <LogOut className="h-4 w-4" />
              {t('userMenu.signOut', 'Sign out')}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* Searches & Alerts Panel */}
      <SearchesAndAlertsPanel
        open={savedSearchesOpen}
        onOpenChange={setSavedSearchesOpen}
        onLoadSearch={handleLoadSearch}
      />

    </>
  )
}
